import { router } from '@inertiajs/react'
import { useState } from 'react'
import { Link } from '@inertiajs/react'
import Navbar from '@/components/layout/Navbar'
import { cn } from '@/lib/utils'
import type { EpisodeCard } from '@/types'

interface Props {
  query: string
  results: EpisodeCard[]
}

export default function Index({ query, results }: Props) {
  const [q, setQ] = useState(query)

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    router.get('/search', { q }, { preserveScroll: true })
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      <div className="max-w-3xl mx-auto px-4 py-12">
        <form onSubmit={handleSearch} className="mb-8">
          <input
            value={q}
            onChange={e => setQ(e.target.value)}
            autoFocus
            placeholder="Search episodes, tags, show notes..."
            className="w-full px-5 py-3 text-base border border-gray-200 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-brand/30"
          />
        </form>

        {query && (
          <p className="text-sm text-gray-400 mb-5">
            {results.length} result{results.length !== 1 ? 's' : ''} for <span className="font-semibold text-gray-700">"{query}"</span>
          </p>
        )}

        <div className="space-y-3">
          {results.map(ep => (
            <Link
              key={ep.id}
              href={`/episodes/${ep.slug}`}
              className="block bg-white rounded-xl border border-gray-100 p-4 hover:border-brand/30 hover:shadow-sm transition-all"
            >
              <div className="flex items-start gap-4">
                {/* Thumbnail */}
                <div className="w-32 aspect-video rounded-lg overflow-hidden shrink-0 bg-gray-800">
                  {ep.thumbnail_url ? (
                    <img src={ep.thumbnail_url} alt={ep.title} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={cn(
                      'text-xs font-medium px-2 py-0.5 rounded-full capitalize',
                      ep.difficulty === 'beginner' ? 'bg-green-100 text-green-700' :
                      ep.difficulty === 'intermediate' ? 'bg-orange-100 text-orange-700' :
                      'bg-red-100 text-red-700'
                    )}>
                      {ep.difficulty}
                    </span>
                    <span className={cn(
                      'text-xs font-medium px-2 py-0.5 rounded-full',
                      ep.is_free ? 'bg-green-100 text-green-700' : 'bg-rose-100 text-rose-600'
                    )}>
                      {ep.is_free ? 'Free' : 'Pro'}
                    </span>
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-0.5">{ep.title}</h3>
                  <p className="text-sm text-gray-500 line-clamp-2">{ep.description}</p>
                  <p className="text-xs text-gray-400 mt-1.5">
                    {ep.duration}
                    {ep.tags.map(t => <span key={t.slug}> · {t.name}</span>)}
                  </p>
                </div>
              </div>
            </Link>
          ))}
        </div>

        {results.length === 0 && query && (
          <div className="text-center py-16 text-gray-400">
            <p className="text-lg mb-2">No results found</p>
            <p className="text-sm">Try a different keyword or browse all episodes</p>
          </div>
        )}
      </div>
    </div>
  )
}
